<template>
  <div id="customer-payment">
    <div class="col-heading">
      <h3 class>
        Enter payment details
      </h3>
    </div>
    <div v-if="!braintreeClient">
      <small class="text--error">Initializing session...</small>
    </div>
    <div v-if="braintreeSessionError && !paymentSessionAvailable">
      <small class="text--error">Payment session unavailable, please refresh</small>
    </div>
    <div v-if="tokenizeError !== null">
      <small class="text--error">{{ tokenizeError }}</small>
    </div>
    <div class="card col-md-6">
      <form
        id="card"
        class="mb-0"
      >
        <div class="mb-2 field field--medium-width">
          <label for="card-number">Card number</label>
          <div class="input input--card">
            <div
              id="card-number"
              class="input__element form-control"
            />
          </div>
        </div>
        <div class="field">
          <div class="field__group mb-4">
            <span class="field__label">
              Expiry date
              <span class="field__label--hint">For example, 07/20</span>
            </span>
            <div class="field field--small-width">
              <label for="expiration-month">Month</label>
              <div class="input">
                <div
                  id="expiration-month"
                  class="input__element form-control"
                />
              </div>
            </div>

            <div class="field field--small-width">
              <label for="expiration-year">Year</label>
              <div class="input">
                <div
                  id="expiration-year"
                  class="input__element form-control"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="field mb-2">
          <label
            for="cvv"
            class="field__label"
          >Security code</label>
          <div class="input input--small-width">
            <div class="input input--small-width">
              <div
                id="cvv"
                class="input__element form-control"
              />
            </div>
          </div>
        </div>

        <div class="field">
          <div class="input input--checkbox group">
            <span class="proxy-label field__label">Terms and conditions</span>
            <input
              id="AcceptedTermsAndConditions"
              v-model="termsAndConditionsAccepted"
              type="checkbox"
              class="input__checkbox"
            >
            <label
              for="AcceptedTermsAndConditions"
              class="input__checkbox-input"
            >Customer has accepted the Terms &amp; Conditions</label>
          </div>
        </div>

        <button
          :disabled="paymentButtonDisabled"
          type="button"
          class="btn btn-lg btn-primary mt-4"
          @click="preparePayNow"
        >
          <div v-if="paymentSessionUpdating">
            <i class="fa fa-spinner fa-spin" />
          </div>Make payment
        </button>
      </form>
    </div>
    <small>Braintree hosted fields</small>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import VS2 from 'vue-script2'

export default {
    name: 'PaymentDetails',
    props: ['disabled'],
    data () {
        return {
            termsAndConditionsAccepted: false,
            paymentSessionUpdating: false,
            loadedSession: false,
            hostedFieldsInstance: null,
            braintreeClient: null,
            braintreeSessionError: false,
            tokenizeError: null
        }
    },
    computed: {
        ...mapState({
            sessionId: state => state.booking.bookingReserveResponse.data.sessionId
        }),
        bookingResponse: {
            get () {
                return this.$store.state.booking.bookingConfirmResponse
            },
            async set (value) {
                await this.$store.dispatch('setBookingCreationResponse', value)
            }
        },
        bookingAttempted () {
            return this.bookingResponse !== null
        },
        bookingSuccessful () {
            return this.bookingAttempted && this.bookingResponse.success
        },
        paymentButtonDisabled () {
            return (
                !this.termsAndConditionsAccepted ||
                (this.paymentSessionUpdating && !this.bookingAttempted) ||
                (this.bookingAttempted && this.bookingSuccessful) ||
                this.disabled
            )
        },
        paymentSessionAvailable () {
            return this.loadedSession
        }
    },
    created () {
        // Load Braintree js
        VS2.load(
            'https://js.braintreegateway.com/web/3.45.0/js/client.min.js'
        ).then(() => {
            VS2.load(
                'https://js.braintreegateway.com/web/3.45.0/js/hosted-fields.min.js'
            ).then(() => {
                this.setupBraintree()
            })
        })
            .catch((err) => {
                this.$toasted.error(err)
            })
    },
    methods: {
        preparePayNow () {
            this.paymentSessionUpdating = true
            this.tokenizeError = null
            this.hostedFieldsInstance.tokenize()
                .then((payload) => {
                    const paymentSession = {
                        sessionId: payload.nonce,
                        merchant: 'Braintree'
                    }
                    this.$store.dispatch('setPaymentSession', paymentSession)
                })
                .catch((errClick) => {
                    this.paymentSessionUpdating = false
                    this.tokenizeError = JSON.stringify(errClick)
                    if (errClick.code === 'HOSTED_FIELDS_FIELDS_INVALID') {
                        let message = 'Invalid payment details '
                        const invalidKeys = errClick.details.invalidFieldKeys
                        invalidKeys.forEach(element => {
                            if (element === 'number') {
                                message += `- Card Number `
                            } else if (element === 'cvv') {
                                message += `- Security Number `
                            } else if (element === 'expirationMonth') {
                                message += `- Expiry Month `
                            } else if (element === 'expirationYear') {
                                message += `- Expiry Year `
                            }
                        })
                        this.tokenizeError = message
                    } else if (errClick.code === 'HOSTED_FIELDS_FIELDS_EMPTY') {
                        this.tokenizeError = 'Enter the payment details'
                    }
                    this.$toasted.error(this.tokenizeError)
                })
        },
        setupBraintree () {
            const componentInstance = this
            // eslint-disable-next-line
            braintree.client.create(
                {
                    authorization: componentInstance.sessionId
                })
                .then((clientInstance) => {
                    componentInstance.braintreeClient = clientInstance

                    componentInstance.initializeBraintreeHostedFields(clientInstance)
                })
                .catch((errinit) => {
                    componentInstance.braintreeSessionError = true
                    this.$toasted.error(errinit)
                })
        },
        initializeBraintreeHostedFields (clientInstance) {
            const componentInstance = this
            // Create Hosted Fields
            braintree.hostedFields.create(
                {
                    client: clientInstance,
                    fields: {
                        number: {
                            selector: '#card-number',
                            placeholder: ''
                        },
                        cvv: {
                            selector: '#cvv',
                            placeholder: ''
                        },
                        expirationMonth: {
                            selector: '#expiration-month',
                            placeholder: ''
                        },
                        expirationYear: {
                            selector: '#expiration-year',
                            placeholder: ''
                        }
                    }
                })
                .then((hostedFieldsInstance) => {
                    componentInstance.hostedFieldsInstance = hostedFieldsInstance
                    componentInstance.loadedSession = true
                })
                .catch((hostedFieldsErr) => {
                    componentInstance.braintreeSessionError = true
                    this.$toasted.error(hostedFieldsErr)
                })
        }
    }
}
</script>
